/**
 * CharacterPreview — Babylon.js live 3D engine for character rendering.
 * All assets loaded from R2/CDN. No local fallbacks.
 *
 * Used by:
 *  - Settings screen (interactive customization)
 *  - Game screens (runner portraits via renderToCanvas)
 *
 * Asset naming follows corpllm-assets output convention (lowercase, no prefix).
 * R2 paths:
 *  - characters/{name}.glb        (e.g. cc_sk_chr_cyborg_female_01.glb)
 *  - attachments/{name}.glb       (e.g. hair_01.glb)
 *  - characters/skin_config.json  (MST skin-tone data)
 */

import { Engine } from '@babylonjs/core/Engines/engine';
import { Scene } from '@babylonjs/core/scene';
import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { Color4 } from '@babylonjs/core/Maths/math.color';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { SceneLoader } from '@babylonjs/core/Loading/sceneLoader';
import { Quaternion } from '@babylonjs/core/Maths/math.vector';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { Skeleton } from '@babylonjs/core/Bones/skeleton';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';

import '@babylonjs/loaders/glTF';
import '@babylonjs/core/Animations/animatable';
import '@babylonjs/core/Materials/PBR';

import { resolveAsset } from '../data/AssetResolver';

// ── Data ─────────────────────────────────────────────────────────────────────

export interface CharacterDef {
  id: string;
  name: string;
  /** GLB filename without extension, e.g. "cc_sk_chr_cyborg_female_01" */
  file: string;
  color: string;
}

export const CHARACTERS: CharacterDef[] = [
  { id: 'cyborg_f', name: 'Cyborg F', file: 'cc_sk_chr_cyborg_female_01', color: '#4a9e7d' },
  { id: 'cyborg_m', name: 'Cyborg M', file: 'cc_sk_chr_cyborg_male_01', color: '#5a7a9e' },
  { id: 'merc_f', name: 'Merc F', file: 'cc_sk_chr_mercenary_female_01', color: '#9e4a5a' },
  { id: 'merc_m', name: 'Merc M', file: 'cc_sk_chr_mercenary_male_01', color: '#7a5a3a' },
  { id: 'racer_f', name: 'Racer F', file: 'cc_sk_chr_racer_female_01', color: '#e07030' },
  { id: 'rich_f', name: 'Rich F', file: 'cc_sk_chr_rich_female_01', color: '#8050a0' },
  { id: 'rich_m', name: 'Rich M', file: 'cc_sk_chr_rich_male_01', color: '#a08040' },
  { id: 'street_m', name: 'Street M', file: 'cc_sk_chr_street_male_01', color: '#607080' },
];

export const MST_TONES = [
  { level: 3, color: '#f7ead0' }, { level: 4, color: '#eadaba' },
  { level: 5, color: '#d7bd96' }, { level: 6, color: '#a07e56' },
  { level: 7, color: '#825c43' }, { level: 8, color: '#604134' },
  { level: 9, color: '#3a312a' }, { level: 10, color: '#292420' },
];

export interface AttachmentDef {
  name: string;
  /** GLB filename without extension, null = unequip */
  file: string | null;
}

export const HAIR: AttachmentDef[] = [
  { name: 'None', file: null },
  { name: 'Hair 01', file: 'hair_01' }, { name: 'Hair 02', file: 'hair_02' },
  { name: 'Hair 03', file: 'hair_03' }, { name: 'Hair 04', file: 'hair_04' },
  { name: 'Hair 05', file: 'hair_05' }, { name: 'Hair 06', file: 'hair_06' },
  { name: 'Hair 07', file: 'hair_07' }, { name: 'Hair 08', file: 'hair_08' },
];

export const HEADGEAR: AttachmentDef[] = [
  { name: 'None', file: null },
  { name: 'Hat 01', file: 'hat_01' }, { name: 'Hat 02', file: 'hat_02' },
  { name: 'Hat 03', file: 'hat_03' }, { name: 'Hat 04', file: 'hat_04' },
];

export const EARRINGS: AttachmentDef[] = [
  { name: 'None', file: null },
  { name: 'Earring 01', file: 'earring_01' },
  { name: 'Earrings F 01', file: 'earrings_female_01' },
  { name: 'Earrings F 02', file: 'earrings_female_02' },
  { name: 'Earrings F 03', file: 'earrings_female_03' },
  { name: 'Earrings F 04', file: 'earrings_female_04' },
  { name: 'Earrings M 01', file: 'earrings_male_01' },
  { name: 'Earrings M 02', file: 'earrings_male_02' },
  { name: 'Earrings M 03', file: 'earrings_male_03' },
];

export const PIERCINGS: AttachmentDef[] = [
  { name: 'None', file: null },
  { name: 'Piercings F 01', file: 'piercings_female_01' },
  { name: 'Piercings F 02', file: 'piercings_female_02' },
  { name: 'Piercings M 01', file: 'piercings_male_01' },
];

export const MASKS: AttachmentDef[] = [
  { name: 'None', file: null },
  { name: 'Mask M 01', file: 'mask_male_01' },
];

const SLOT_EXCLUSION: Record<string, string[]> = {
  hair: ['hair', 'headgear'],
  headgear: ['hair', 'headgear'],
};

const POSE_DEFS: Record<string, Record<string, { rx?: number; ry?: number; rz?: number }>> = {
  t: {},
  a: {
    upperarm_l: { ry: 30 },
    upperarm_r: { ry: 30 },
  },
  i: {
    upperarm_l: { ry: 70 },
    upperarm_r: { ry: 70 },
    lowerarm_l: { ry: 10 },
    lowerarm_r: { ry: 10 },
  },
};

const ATTACH_DEFAULTS = { scale: 100, rotX: -90, rotY: 90, rotZ: -180, ox: 0, oy: 0, oz: 0 };

// ── Skin Config (loaded once from CDN) ───────────────────────────────────────

type SkinConfig = Record<string, { pickedColor: number[]; tolerance: number }>;
let skinConfigPromise: Promise<SkinConfig> | null = null;

function loadSkinConfig(): Promise<SkinConfig> {
  if (!skinConfigPromise) {
    skinConfigPromise = fetch(resolveAsset('characters/skin_config'))
      .then(r => {
        if (!r.ok) throw new Error(`skin_config.json: ${r.status} ${r.statusText}`);
        return r.json() as Promise<SkinConfig>;
      });
  }
  return skinConfigPromise;
}

// ── Preview Engine ───────────────────────────────────────────────────────────

export class CharacterPreview {
  private engine: Engine;
  private scene: Scene;
  private camera: ArcRotateCamera;
  private canvas: HTMLCanvasElement;

  private currentMeshes: AbstractMesh[] = [];
  private currentSkeleton: Skeleton | null = null;
  private currentRootMesh: AbstractMesh | null = null;
  private currentCharFile: string | null = null;

  private equippedAttachments: Record<string, AbstractMesh> = {};
  /** Multi-slot items keyed by "slot:filename" */
  private multiAttachments: Record<string, AbstractMesh> = {};
  private restPoseMap: Record<string, Quaternion> = {};
  private currentPose = 't';

  // MST skin-tone state
  private currentTexture: { _texture: unknown; getSize(): { width: number; height: number }; readPixels(): Promise<Uint8Array> } | null = null;
  private originalTextureData: Uint8Array | null = null;
  private skinConfig: SkinConfig = {};

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    this.engine = new Engine(canvas, true, { antialias: true });
    // Babylon sets a positive tabindex on the canvas (a11y anti-pattern —
    // positive tabindex hijacks tab order). Keep it focusable at tabindex 0.
    this.canvas.tabIndex = 0;
    this.scene = new Scene(this.engine);
    this.scene.clearColor = new Color4(0.086, 0.086, 0.18, 1);

    this.camera = new ArcRotateCamera('cam', Math.PI / 2, 1.2, 3, new Vector3(0, 0.9, 0), this.scene);
    this.camera.attachControl(canvas, true);
    this.camera.lowerRadiusLimit = 0.3;
    this.camera.upperRadiusLimit = 6;
    this.camera.wheelDeltaPercentage = 0.02;
    this.camera.panningSensibility = 400;
    this.camera.pinchDeltaPercentage = 0.01;

    const hemi = new HemisphericLight('hemi', new Vector3(0.5, 1, 0.3), this.scene);
    hemi.intensity = 1.2;
    const fill = new HemisphericLight('fill', new Vector3(-0.5, -0.2, -1), this.scene);
    fill.intensity = 0.4;

    this.engine.runRenderLoop(() => this.scene.render());

    // Load skin config from CDN
    loadSkinConfig()
      .then(cfg => { this.skinConfig = cfg; })
      .catch(e => console.error('[CharacterPreview] Failed to load skin_config:', e));
  }

  resize(): void {
    this.engine.resize();
  }

  /** Animate camera to face level (close-up portrait view) */
  zoomToFace(): void {
    const headBone = this.currentSkeleton?.bones.find(b => b.name === 'head');
    let targetY = 1.55; // sensible default head height
    if (headBone) {
      const tn = headBone.getTransformNode?.();
      if (tn) targetY = tn.absolutePosition.y;
    }
    this.animateCamera(Math.PI / 2, 1.5, 0.18, new Vector3(0, targetY + 0.02, 0));
  }

  /** Reset camera to default full-body view */
  resetCamera(): void {
    this.animateCamera(Math.PI / 2, 1.2, 3, new Vector3(0, 0.9, 0));
  }

  private animateCamera(alpha: number, beta: number, radius: number, target: Vector3): void {
    const frames = 30;
    const startAlpha = this.camera.alpha;
    const startBeta = this.camera.beta;
    const startRadius = this.camera.radius;
    const startTarget = this.camera.target.clone();
    let frame = 0;

    const step = () => {
      frame++;
      const t = frame / frames;
      const ease = t * t * (3 - 2 * t); // smoothstep
      this.camera.alpha = startAlpha + (alpha - startAlpha) * ease;
      this.camera.beta = startBeta + (beta - startBeta) * ease;
      this.camera.radius = startRadius + (radius - startRadius) * ease;
      this.camera.target = Vector3.Lerp(startTarget, target, ease);
      if (frame < frames) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }

  /** Get the currently loaded character file name (without extension) */
  getCurrentCharFile(): string | null {
    return this.currentCharFile;
  }

  /**
   * Render a 512×512 head portrait as base64 data URL (JPEG).
   * Temporarily moves camera to face position, renders one frame to an offscreen canvas, then restores.
   */
  async renderPortrait(): Promise<string> {
    // Save current camera state
    const savedAlpha = this.camera.alpha;
    const savedBeta = this.camera.beta;
    const savedRadius = this.camera.radius;
    const savedTarget = this.camera.target.clone();

    // Move camera to face
    const headBone = this.currentSkeleton?.bones.find(b => b.name === 'head');
    let targetY = 1.55;
    if (headBone) {
      const tn = headBone.getTransformNode?.();
      if (tn) targetY = tn.absolutePosition.y;
    }
    this.camera.alpha = Math.PI / 2;
    this.camera.beta = 1.5;
    this.camera.radius = 0.3;
    this.camera.target = new Vector3(0, targetY, 0);

    // Render one frame
    this.scene.render();
    await new Promise(r => setTimeout(r, 50));
    this.scene.render();

    // Capture from the main canvas
    const size = 512;
    const offscreen = document.createElement('canvas');
    offscreen.width = size;
    offscreen.height = size;
    const ctx = offscreen.getContext('2d')!;

    // Crop a tight square around the face from the center of the canvas.
    // At radius 0.18 the face fills roughly 40% of the shorter axis.
    const src = this.canvas;
    const cropSize = Math.round(Math.min(src.width, src.height) * 0.38);
    const sx = Math.round((src.width  - cropSize) / 2);
    const sy = Math.round((src.height - cropSize) / 2);
    ctx.drawImage(src, sx, sy, cropSize, cropSize, 0, 0, size, size);

    const dataUrl = offscreen.toDataURL('image/jpeg', 0.92);

    // Restore camera
    this.camera.alpha = savedAlpha;
    this.camera.beta = savedBeta;
    this.camera.radius = savedRadius;
    this.camera.target = savedTarget;

    return dataUrl;
  }

  dispose(): void {
    this.engine.stopRenderLoop();
    this.scene.dispose();
    this.engine.dispose();
  }

  // ── Character loading ────────────────────────────────────────────────────

  async selectCharacter(ch: CharacterDef): Promise<void> {
    // Remove old meshes
    this.currentMeshes.forEach(m => m.dispose());
    this.currentMeshes = [];

    // Clear attachments
    Object.values(this.equippedAttachments).forEach(m => m.dispose());
    this.equippedAttachments = {};
    Object.values(this.multiAttachments).forEach(m => m.dispose());
    this.multiAttachments = {};

    // Reset MST
    this.originalTextureData = null;
    this.currentTexture = null;

    const result = await SceneLoader.ImportMeshAsync('', resolveAsset(`characters/${ch.file}`), '', this.scene);
    this.currentMeshes = result.meshes;
    this.currentCharFile = ch.file;

    this.currentSkeleton = result.skeletons[0] ?? null;
    this.currentRootMesh = result.meshes.find(m => m.getTotalVertices() > 0) ?? result.meshes[0];

    // Force opaque + find main texture
    for (const m of result.meshes) {
      if (m.material) {
        (m.material as PBRMaterial).transparencyMode = 0;
        m.material.alpha = 1;
        const albedo = (m.material as PBRMaterial).albedoTexture;
        if (albedo && !this.currentTexture) {
          (albedo as { hasAlpha: boolean }).hasAlpha = false;
          this.currentTexture = albedo as unknown as typeof this.currentTexture;
        }
      }
    }

    // Wait for scene to settle, then capture texture for MST
    await new Promise(r => setTimeout(r, 500));
    if (this.currentTexture) {
      await this.captureOriginalTexture();
    }

    this.captureRestPose();
    if (this.currentPose !== 't') this.applyPose(this.currentPose);
  }

  // ── MST (Skin tone) ─────────────────────────────────────────────────────

  private async captureOriginalTexture(): Promise<void> {
    if (!this.currentTexture) return;
    const gpuPixels = await this.currentTexture.readPixels();
    if (!gpuPixels || gpuPixels.length === 0) {
      console.error('[CharacterPreview] readPixels returned empty data');
      return;
    }
    const size = this.currentTexture.getSize();
    const w = size.width, h = size.height;
    const flipped = new Uint8Array(gpuPixels.length);
    const rowBytes = w * 4;
    for (let y = 0; y < h; y++) {
      const srcOff = y * rowBytes;
      const dstOff = (h - 1 - y) * rowBytes;
      flipped.set(gpuPixels.subarray(srcOff, srcOff + rowBytes), dstOff);
    }
    this.originalTextureData = flipped;
  }

  applySkinTone(hexColor: string): void {
    if (!this.currentCharFile || !this.originalTextureData || !this.currentTexture) {
      console.error('[CharacterPreview] applySkinTone: not ready', {
        file: this.currentCharFile, tex: !!this.originalTextureData, cur: !!this.currentTexture,
      });
      return;
    }
    // skin_config keys use .glb suffix
    const config = this.skinConfig[`${this.currentCharFile}.glb`];
    if (!config?.pickedColor) {
      console.error('[CharacterPreview] No skin config for', this.currentCharFile);
      return;
    }

    const [sr, sg, sb] = config.pickedColor;
    const tolerance = config.tolerance ?? 35;
    const [tr, tg, tb] = hexToRgb(hexColor);
    const size = this.currentTexture.getSize();
    const w = size.width, h = size.height;
    const pixels = new Uint8Array(this.originalTextureData.length);
    pixels.set(this.originalTextureData);

    for (let i = 0; i < pixels.length; i += 4) {
      const dist = colorDistance(pixels[i], pixels[i + 1], pixels[i + 2], sr, sg, sb);
      if (dist <= tolerance) {
        const origLum = (0.299 * pixels[i] + 0.587 * pixels[i + 1] + 0.114 * pixels[i + 2]) / 255;
        const targetLum = (0.299 * tr + 0.587 * tg + 0.114 * tb) / 255;
        const lumRatio = targetLum > 0.01 ? origLum / targetLum : 1;
        const factor = 0.7 + 0.3 * Math.min(lumRatio, 1.5);
        pixels[i] = Math.min(255, Math.round(tr * factor));
        pixels[i + 1] = Math.min(255, Math.round(tg * factor));
        pixels[i + 2] = Math.min(255, Math.round(tb * factor));
      }
    }

    // Flip Y back to bottom-up for GPU upload
    const gpuPixels = new Uint8Array(pixels.length);
    const rowBytes = w * 4;
    for (let y = 0; y < h; y++) {
      gpuPixels.set(pixels.subarray(y * rowBytes, y * rowBytes + rowBytes), (h - 1 - y) * rowBytes);
    }
    const eng = this.scene.getEngine();
    (eng as unknown as { updateTextureData(t: unknown, d: Uint8Array, x: number, y: number, w: number, h: number): void })
      .updateTextureData((this.currentTexture as unknown as { _texture: unknown })._texture, gpuPixels, 0, 0, w, h);
  }

  // ── Pose ──────────────────────────────────────────────────────────────────

  private captureRestPose(): void {
    this.restPoseMap = {};
    if (!this.currentSkeleton) {
      console.error('[CharacterPreview] No skeleton for rest pose capture');
      return;
    }
    for (const bone of this.currentSkeleton.bones) {
      const tn = bone.getTransformNode?.();
      if (tn?.rotationQuaternion) {
        this.restPoseMap[bone.name] = tn.rotationQuaternion.clone();
      } else if (bone.rotationQuaternion) {
        this.restPoseMap[bone.name] = bone.rotationQuaternion.clone();
      }
    }
  }

  applyPose(poseId: string): void {
    if (!this.currentSkeleton) {
      console.error('[CharacterPreview] applyPose: no skeleton');
      return;
    }
    if (Object.keys(this.restPoseMap).length === 0) this.captureRestPose();

    const deltas = POSE_DEFS[poseId] ?? {};
    for (const bone of this.currentSkeleton.bones) {
      const tn = bone.getTransformNode?.();
      const restQ = this.restPoseMap[bone.name] ?? Quaternion.Identity();
      const d = deltas[bone.name];

      const finalQ = d
        ? restQ.multiply(Quaternion.FromEulerAngles(deg(d.rx ?? 0), deg(d.ry ?? 0), deg(d.rz ?? 0)))
        : restQ.clone();

      if (tn?.rotationQuaternion) {
        tn.rotationQuaternion.copyFrom(finalQ);
      } else if (bone.rotationQuaternion) {
        bone.rotationQuaternion.copyFrom(finalQ);
      }
    }
    this.currentPose = poseId;
  }

  // ── Attachments ───────────────────────────────────────────────────────────

  async equipAttachment(slot: string, item: AttachmentDef): Promise<void> {
    const exclusions = SLOT_EXCLUSION[slot] ?? [slot];
    for (const s of exclusions) {
      if (this.equippedAttachments[s]) {
        this.equippedAttachments[s].dispose();
        delete this.equippedAttachments[s];
      }
    }
    if (!item.file || !this.currentSkeleton) return;

    const result = await SceneLoader.ImportMeshAsync('', resolveAsset(`attachments/${item.file}`), '', this.scene);
    const root = result.meshes[0];

    const bone = this.currentSkeleton.bones.find(b => b.name === 'head');
    if (!bone) {
      console.error('[CharacterPreview] head bone not found');
      return;
    }

    const attachRoot = this.currentMeshes[0] ?? this.currentRootMesh;
    if (!attachRoot) {
      console.error('[CharacterPreview] no root mesh for attachment');
      return;
    }

    root.attachToBone(bone, attachRoot as AbstractMesh);
    const d = ATTACH_DEFAULTS;
    const rootZ = (attachRoot as TransformNode).scaling?.z ?? 1;
    const zFlip = rootZ < 0 ? -1 : 1;
    root.scaling = new Vector3(d.scale, d.scale, zFlip * d.scale);
    root.rotation = new Vector3(
      d.rotX * Math.PI / 180,
      d.rotY * Math.PI / 180,
      d.rotZ * Math.PI / 180,
    );
    root.position = new Vector3(d.ox * 0.1, d.oy * 0.1, d.oz * 0.1);

    // Force opaque
    for (const m of result.meshes) {
      if (m.material) {
        (m.material as PBRMaterial).transparencyMode = 0;
        m.material.alpha = 1;
      }
    }

    this.equippedAttachments[slot] = root;
  }

  /**
   * Toggle an attachment in a multi-select slot (e.g. piercings).
   * Returns true if item was equipped, false if it was unequipped.
   */
  async toggleAttachment(slot: string, item: AttachmentDef): Promise<boolean> {
    if (!item.file) {
      // "None" = clear all in this slot
      this.clearMultiSlot(slot);
      return false;
    }

    const key = `${slot}:${item.file}`;
    if (this.multiAttachments[key]) {
      // Already equipped → unequip
      this.multiAttachments[key].dispose();
      delete this.multiAttachments[key];
      return false;
    }

    // Equip new item
    if (!this.currentSkeleton) return false;
    const result = await SceneLoader.ImportMeshAsync('', resolveAsset(`attachments/${item.file}`), '', this.scene);
    const root = result.meshes[0];

    const bone = this.currentSkeleton.bones.find(b => b.name === 'head');
    if (!bone) return false;

    const attachRoot = this.currentMeshes[0] ?? this.currentRootMesh;
    if (!attachRoot) return false;

    root.attachToBone(bone, attachRoot as AbstractMesh);
    const d = ATTACH_DEFAULTS;
    const rootZ = (attachRoot as TransformNode).scaling?.z ?? 1;
    const zFlip = rootZ < 0 ? -1 : 1;
    root.scaling = new Vector3(d.scale, d.scale, zFlip * d.scale);
    root.rotation = new Vector3(d.rotX * Math.PI / 180, d.rotY * Math.PI / 180, d.rotZ * Math.PI / 180);
    root.position = new Vector3(d.ox * 0.1, d.oy * 0.1, d.oz * 0.1);

    for (const m of result.meshes) {
      if (m.material) {
        (m.material as PBRMaterial).transparencyMode = 0;
        m.material.alpha = 1;
      }
    }

    this.multiAttachments[key] = root;
    return true;
  }

  /** Clear all items in a multi-select slot */
  clearMultiSlot(slot: string): void {
    const prefix = `${slot}:`;
    for (const key of Object.keys(this.multiAttachments)) {
      if (key.startsWith(prefix)) {
        this.multiAttachments[key].dispose();
        delete this.multiAttachments[key];
      }
    }
  }

  /** Check if a multi-slot item is currently equipped */
  isMultiEquipped(slot: string, file: string): boolean {
    return !!this.multiAttachments[`${slot}:${file}`];
  }

  resetAttachments(): void {
    Object.values(this.equippedAttachments).forEach(m => m.dispose());
    this.equippedAttachments = {};
    Object.values(this.multiAttachments).forEach(m => m.dispose());
    this.multiAttachments = {};
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function deg(v: number): number {
  return v * Math.PI / 180;
}

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
  ];
}

function colorDistance(r1: number, g1: number, b1: number, r2: number, g2: number, b2: number): number {
  return Math.sqrt(2 * (r1 - r2) ** 2 + 4 * (g1 - g2) ** 2 + 3 * (b1 - b2) ** 2);
}
